import { Component } from 'substance'
import isEqual from 'lodash/isEqual'

/**
 * @typedef UIImageCropper.props
 * @property {string} parentId - imageNode
 * @property {string} src - asdf
 * @property {object} configuredCrops - asdf
 * @property {number} width - imageNode.width
 * @property {number} height - imageNode.height
 * @property {object} [crops=[]] - imageNode.crops.crops, crop definitions. Defaults to empty array
 * @property {boolean} [disableAutomaticCrop=false] - imageNode.disableAutomaticCrop
 * @property {function} abort - Called before Image Cropper closes
 * @property {function} restore - Called when clicking "Restore All"-button
 * @property {function} save - Called after defined crops have been mapped
 */

/**
 * @typedef UIImageCropper.state
 * @property {string} [disableAutomaticCrop=false]    Disables automatic crop
 */

/**
 * An editor component that lets the user edit text fields on a node.
 *
 * @name UIImageCropper
 * @class
 *
 * @example
 * import {UIImageCropper, api} from 'writer'
 *
 * render($$) {
 *     const imageNode = this.props.node
 *     const cropperOverlay = $$('div').ref('cropperOverlay').addClass('hidden')
 *
 *     imageNode.fetchSpecifiedUrls(['service', 'original'])
 *         .then((src) => {
 *              const cropper = $$(ImageCropperComponent, {
 *                  parentId: imageNode,
 *                  src,
 *                  configuredCrops: {"16:9": [16,9], "1:1": [1,1]},
 *                  width: imageNode.width,
 *                  height: imageNode.height,
 *                  crops: imageNode.crops.crops || [],
 *                  disableAutomaticCrop: imageNode.disableAutomaticCrop,
 *                  abort: () => {
 *                      console.info('handle abort')
 *                  },
 *                  restore: () => {
 *                      console.info('handle restore')
 *                      return false
 *                  },
 *                  save: (newCrops, disableAutomaticCrop) => {
 *                      console.info('handle the formatted crops', newCrops)
 *                  }
 *              })
 *              this.refs.cropperOverlay.removeClass('hidden')
 *              this.refs.cropperOverlay.append(cropper)
 *          })
 *          .catch(err => {
 *              console.error(err)
 *              api.ui.showMessageDialog([{
 *                  type: 'error',
 *                  message: `${this.getLabel('The image doesn\'t seem to be available just yet. Please wait a few seconds and try again.')}\n\n${err}`
 *              }])
 *          })
 * }
 *
 * @param {UIImageCropper.props} props
 * @property {UIImageCropper.props} props
 * @property {UIImageCropper.state} state
 */
class ImageCropperComponent extends Component {

    getInitialState() {
        return {
            disableAutomaticCrop: this.props.disableAutomaticCrop === true,
            hideDisableCropsCheckbox: this.props.hideDisableCropsCheckbox === true
        }
    }

    // Never rerender this component as the crop looses it
    shouldRerender(newProps, newState) { // eslint-disable-line
        return false
    }

    didMount() {
        this.cropEditor = new IMSoftcrop.Editor( // eslint-disable-line
            'image-cropper__softcrop',
            {
                autocrop: false,
                //detectWorkerUrl: false, // FIXME: Use 'node_modules/cropjs/dist/js/imcrop.worker.detect.js',
                //detectThreshold: 30,
                //detectStepSize: 3,
                debug: false
            }
        )
        // Make sure esc works in the crop editor
        this.cropEditor.onCancel(this.onClose.bind(this))

        let configuredCrops = this.props.configuredCrops || [],
            encodedSrc = encodeURIComponent(this.props.src)

        this.disableAutomaticCrop = this.props.disableAutomaticCrop

        this.cropEditor.addImage(
            '/api/resourceproxy?url=' + encodedSrc,
            () => {
                let selected = true
                for(const name in configuredCrops) {
                    if (this.props.crops) {
                        this.addCrop(name, selected, configuredCrops[name], this.props.crops)
                    }
                    else {
                        this.createCrop(name, selected, configuredCrops[name])
                    }
                    selected = false
                }
            }
        )
    }

    createCrop(name, selected, definedCrop) {
        // If there are no defined crops, new crops should be usable by default
        const usable = this.props.crops.length === 0

        this.cropEditor.addSoftcrop(
            name,
            selected,
            definedCrop[0],
            definedCrop[1],
            null,
            null,
            usable
        )
    }

    // FIXME: Does not work with existing crops already on the image
    addCrop(name, selected, definedCrop, existingCrops) {
        var existingCrop = null
        for (var n = 0; n < existingCrops.length; n++) {
            if (existingCrops[n].name === name) {
                existingCrop = existingCrops[n]
                break
            }
        }

        if (!existingCrop) {
            this.createCrop(name, selected, definedCrop)
            return
        }

        // Find actual with used, "w" is used by Imgix, "width" by Imengine
        let matches = this.props.src.match(/&(w|width)=([0-9]*)/)
        let imageWidth = this.props.width
        let imageHeight = this.props.height

        if (Array.isArray(matches) && matches.length === 3) {
            imageWidth = matches[2]
            imageHeight = Math.round((imageWidth / this.props.width) * this.props.height)
        }

        this.cropEditor.addSoftcrop(
            name,
            selected,
            Math.round(existingCrop.width * imageWidth),
            Math.round(existingCrop.height * imageHeight),
            Math.round(existingCrop.x * imageWidth),
            Math.round(existingCrop.y * imageHeight)
        )
    }

    render($$) {
        const Toggle = this.context.configurator.getComponent('UIToggle')
        const cropActions = $$('div').addClass('se-crop-actions');
        const softCropper = $$('div')
            .attr('id', 'image-cropper__softcrop')
            .addClass('sc-image-cropper')
            .ref('cropper')

        const cropToggle = $$(Toggle, {
            id: 'crop-toggle',
            alignRight: true,
            checkbox: true,
            label: this.getLabel('Disable automatic crop in frontend'),
            checked: this.state.disableAutomaticCrop,
            onToggle: (checked) => {
                this.extendState({
                    disableAutomaticCrop: checked
                })
            }
        })

        const cropOptions = this.state.hideDisableCropsCheckbox ? $$('div') : $$('div').append(cropToggle).addClass('se-crop-options')
        const restoreAll = $$('button').addClass('btn btn-secondary').append(this.getLabel('Restore all')).on('click', this.props.restore)
        const abort = $$('button').addClass('btn pull-right btn-abort').append(this.getLabel('cancel')).on('click', this.onClose)
        const confirm = $$('button').addClass('btn pull-right btn-primary').append(this.getLabel('Crop')).on('click', this.onSave)
        cropActions.append([
            restoreAll,
            confirm,
            abort,
        ])

        return $$('div').append([
            softCropper,
            cropOptions,
            cropActions
        ]).addClass('se-image-cropper-meta')
    }

    onClose() {
        this.props.abort()
        this.remove()
    }

    onSave() {
        const data = this.cropEditor.getSoftcropData()
        const crops = []
        data.crops.forEach( (crop) => {
            if (!crop.usable) { return }
            crops.push({
                name: crop.id,
                x: crop.x / data.width,
                y: crop.y / data.height,
                width: crop.width / data.width,
                height: crop.height / data.height
            })
        })

        const disableAutomaticCropUpdated = this.state.disableAutomaticCrop !== this.disableAutomaticCrop
        const cropsUpdated = !isEqual(this.props.crops, crops)

        if (cropsUpdated || disableAutomaticCropUpdated) {
            this.props.save({ crops: crops }, this.state.disableAutomaticCrop);
        } else {
            this.onClose()
        }
    }
}

export {ImageCropperComponent}
